import { useMutation, useQuery } from "@apollo/react-hooks";
import gql from "graphql-tag";
import { useLocation, useParams } from "react-router-dom";
import Permissions from "../configs/permissions";
import useItemCounter from "../hooks/itemCounter";
import usePermissions from "../hooks/permissions";
import SurveyListActions from "./../components/list-secondary-actions/SurveyListActions";
import { SURVEY_MODEL_NAME } from "./../configs/constants";
import { publishSurveysMutation } from "./../graphql/mutations/vabooSurveyMutations";
import { allSurveys } from "./../graphql/queries/vabooSurveyQueries";
import useBaseNavigation from "./../hooks/baseNavigation";
import useModal from "./../hooks/modal";
import useNotification from "./../hooks/notifications";
import useListSelection from "./../hooks/rowSelection";

import {
  AddButton,
  ListItemCheckbox,
  TEditButton,
} from "./../mood-ui/Controls";
import {
  Pagination,
  TableContainer,
  TableSelectedActions,
  TableView,
  TCol,
  TContent,
  THCol,
  THeading,
  TRow,
} from "./../mood-ui/DataTable";

import { PageControls } from "../mood-ui/Controls";
import { BlockContainer, Col, PageContainer, Row } from "./../mood-ui/Layout";
import { ConfirmActionModal } from "./../mood-ui/Modals";
import {
  ErrorNotification,
  SuccessNotification,
} from "./../mood-ui/Notifications";
import PageSpinner from "./../mood-ui/PageSpinner";
import Search from "./../mood-ui/Search";
import { ColorLabel, Heading, ItemsNotFound } from "./../mood-ui/Typography";

const ALL_SURVEYS = gql(allSurveys);
const PUBLISH_SURVEYS = gql(publishSurveysMutation);

const SurveyList = () => {
  const { protectPage, hasAccess } = usePermissions();
  protectPage(Permissions.Query.Surveys);
  const {
    selectedRows,
    isRowSelected: rowSelected,
    toggleRow: toggleRowSelection,
    clearSelection,
  } = useListSelection();

  const {
    notify: displayNotification,
    notificationState,
    handleClose,
  } = useNotification();

  const {
    notify: displayErrorNotification,
    notificationState: errorNotificationState,
    handleClose: handleCloseError,
  } = useNotification();

  document.title = "Surveys";

  const { page, limit, orderBy, orderHow, search } = useParams();
  const { search: queryString } = useLocation();
  const { modalState, showModal, closeModal } = useModal();
  const { data, loading, error } = useQuery(ALL_SURVEYS, {
    variables: {
      page: parseInt(page),
      limit: parseInt(limit),
      orderBy: "id",
      orderHow: "desc",
      search,
      queryString,
    },
    fetchPolicy: "cache-and-network",
  });
  const [publishSurveys, { loading: publishingSurveys }] = useMutation(
    PUBLISH_SURVEYS,
    {
      onCompleted: (result) => {
        displayNotification({
          heading: "Surveys Published",
          message: "Successfully published selected surveys",
          secondsToDisplay: 1,
        });
        clearSelection();
      },
      onError: (e) => {
        displayErrorNotification({
          heading: "Error",
          message: "Failed to publish selected surveys",
          secondsToDisplay: 2,
        });
      },
    }
  );

  const { itemsTotal } = useItemCounter(data, { searchKey: "surveys" });

  const { nextPage, prevPage, editForm, createForm, applySearch } =
    useBaseNavigation(SURVEY_MODEL_NAME, { showingItemsCount: itemsTotal });

  const confirmPublishing = () => {
    showModal({
      heading: "Confirm Publishing",
      message:
        "Are you sure you want to PUBLISH selected surveys? They will become available for usage in App/Landlord survey settings, but you will not be able to edit them.",
      mood: "Info",
      confirmButtonLabel: "Publish",
      onConfirm: () => {
        closeModal();
        handlePublishSurveys();
      },
      onCancel: () => {
        closeModal();
      },
    });
  };

  const handlePublishSurveys = () => {
    publishSurveys({ variables: { ids: selectedRows } });
  };

  if (error) return <div>Error</div>;

  return (
    <PageContainer>
      <BlockContainer raised>
        <Row tweaks="border-b border-[#dfe2e8] pb-5 mx-5">
          <Col width={12}>
            <Heading
              inset={true}
              text="Surveys"
              description="Your Surveys — Setup, Publish, and Assign"
            />
          </Col>
        </Row>
        <Row tweaks="pt-5 pb-4 mt-0 px-5">
          <Col width={9}>
            {hasAccess(Permissions.Mutation.CreateSurvey) && (
              <AddButton
                label="Add Survey"
                onClick={createForm}
                loading={loading}
              />
            )}
          </Col>
          <Col width={3}>
            <Search onApplySearch={applySearch} />
          </Col>
        </Row>

        <TableContainer tweaks="px-5 pb-5">
          <TableView>
            <THeading>
              <TRow>
                <THCol tweaks="w-4 text-center" />
                <THCol tweaks="text-left" name="id" value="ID" />
                <THCol tweaks="text-left" name="title" value="Survey" />
                <THCol tweaks="text-left" name="type" value="Type" />
                <THCol tweaks="text-left" name="status" value="Status" />
                <THCol tweaks="w-16 text-center" />
              </TRow>
            </THeading>

            {!data && (
              <tbody className="w-full">
                <tr>
                  <td colSpan="100%">
                    <PageSpinner inline />
                  </td>
                </tr>
              </tbody>
            )}
            {data && data.surveys && data.surveys.length === 0 && (
              <tbody className="w-full">
                <tr>
                  <td colSpan="100%">
                    <ItemsNotFound text="No surveys found" tweaks="" />
                  </td>
                </tr>
              </tbody>
            )}

            {data && data.surveys && data.surveys.length > 0 && (
              <TContent>
                {data.surveys.map((survey) => (
                  <TRow key={survey.id} isSelected={rowSelected(survey.id)}>
                    <TCol>
                      <ListItemCheckbox
                        onChange={() => toggleRowSelection(survey.id)}
                        isChecked={rowSelected(survey.id) ? true : false}
                      />
                    </TCol>
                    <TCol tweaks="max-w-[30px]" value={survey.id} />
                    <TCol tweaks="max-w-[120px]">
                      <abbr title={survey.title}>{survey.title}</abbr>
                    </TCol>
                    <TCol tweaks="max-w-[240px]">
                      <abbr title={survey.survey_type.name}>
                        {survey.survey_type.name}
                      </abbr>
                    </TCol>

                    <TCol tweaks="max-w-[50px]">
                      {survey.status == "Published" ? (
                        <ColorLabel text={survey.status} color="green" />
                      ) : (
                        <ColorLabel text={survey.status} color="gray" />
                      )}
                    </TCol>
                    <TCol tweaks="w-16 text-center">
                      {hasAccess(Permissions.Query.Survey) && (
                        <TEditButton onClick={() => editForm(survey.id)} />
                      )}
                    </TCol>
                  </TRow>
                ))}
              </TContent>
            )}
          </TableView>
          <PageControls spaced>
            <Row tweaks="w-full pl-5">
              <Col width={2}>
                <TableSelectedActions>
                  {selectedRows && selectedRows.length > 0 && (
                    <SurveyListActions
                      onClearSelection={clearSelection}
                      onPublish={confirmPublishing}
                    />
                  )}
                </TableSelectedActions>
              </Col>
              <Col width={7} extraStyles=""></Col>
              <Col width={3}>
                <Pagination
                  onPrev={page > 1 ? prevPage : false}
                  onNext={data?.surveys?.length >= limit - 1 ? nextPage : false}
                />
              </Col>
            </Row>
          </PageControls>
        </TableContainer>
      </BlockContainer>
      <ConfirmActionModal {...modalState} />
      <SuccessNotification {...notificationState} onClose={handleClose} />
      <ErrorNotification
        {...errorNotificationState}
        onClose={handleCloseError}
      />
    </PageContainer>
  );
};

export default SurveyList;
